import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BpsGlobalLogo from '../Logo/BpsGlobal.jpeg';

// Helper function to load image as base64
const loadImageAsBase64 = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Generate International Purchase Order PDF
 */
export const generateInternationalPOPdf = async (poData, details, supplier, branchDetails, countryOfOrigin, termsDetails) => {
  const doc = new jsPDF('p', 'mm', 'a4', true);
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;

  // Colors
  const navy = [30, 58, 95]; // #1e3a5f
  const darkText = [30, 41, 59];
  const grayText = [100, 116, 139];

  const currencyName = poData.CurrencyName || supplier?.CurrencyName || '';
  const currencyCode = currencyName.toUpperCase().trim();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  let yPos = margin;

  // === LOGO & COMPANY HEADER ===
  try {
    const logoBase64 = await loadImageAsBase64(BpsGlobalLogo);
    doc.addImage(logoBase64, 'JPEG', margin, yPos, 24, 24);
  } catch (e) {
    console.error('Logo load failed:', e);
  }

  // Company Name - next to logo
  const companyName = branchDetails.companyName || branchDetails.branchName || '';
  if (companyName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...navy);
    doc.text(companyName, margin + 27, yPos + 10);
  }

  // Branch details - right side
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  if (branchDetails.branchName) {
    doc.text(branchDetails.branchName, pageWidth - margin, yPos + 4, { align: 'right' });
  }
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  let rightY = yPos + 9;
  if (branchDetails.addressLine1) { doc.text(branchDetails.addressLine1, pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }
  if (branchDetails.addressLine2) { doc.text(branchDetails.addressLine2, pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }
  if (branchDetails.location || branchDetails.pincode) { doc.text((branchDetails.location || '') + (branchDetails.pincode ? ' - ' + branchDetails.pincode : ''), pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }
  if (branchDetails.gstNumber) { doc.text('TRN: ' + branchDetails.gstNumber, pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }

  yPos += 28;

  // Separator line
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // === TITLE ===
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('International Purchase Order', pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;
  doc.setLineWidth(0.8);
  const titleWidth = doc.getTextWidth('International Purchase Order');
  doc.line((pageWidth - titleWidth) / 2, yPos, (pageWidth + titleWidth) / 2, yPos);
  yPos += 8;

  // === PO DETAILS (left) + SUPPLIER (right) ===
  const halfWidth = contentWidth / 2 - 2;
  const boxStartY = yPos;

  // Left box: PO Details
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.4);
  doc.rect(margin, boxStartY, halfWidth, 42);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('PURCHASE ORDER DETAILS', margin + 3, boxStartY + 5);
  doc.line(margin, boxStartY + 7, margin + halfWidth, boxStartY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.setFontSize(7.5);
  let leftY = boxStartY + 12;
  const lineH = 4.5;
  const poLabelX = margin + 3;
  const poDataX = margin + 38;
  doc.setFont('helvetica', 'bold'); doc.text('PO No.:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.PurchaseOrderNo || '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('PO Date:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(formatDate(poData.PurchaseOrderDate), poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Incoterms\u00AE 2020:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.Incoterms || '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Currency:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(currencyCode || '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Payment Terms:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.PaymentTerms || '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Shipment Date:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.RequestedShipmentDate ? formatDate(poData.RequestedShipmentDate) : '-', poDataX, leftY);

  // Right box: Supplier
  const rightX = margin + halfWidth + 4;
  doc.setDrawColor(...navy);
  doc.rect(rightX, boxStartY, halfWidth, 42);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('SUPPLIER / SHIPPER (SELLER)', rightX + 3, boxStartY + 5);
  doc.line(rightX, boxStartY + 7, rightX + halfWidth, boxStartY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.setFontSize(7.5);
  let sY = boxStartY + 12;
  const supLabelX = rightX + 3;
  const supDataX = rightX + 28;
  const supplierName = poData.MasterName || supplier?.Name || '-';
  const supplierAddr = (supplier?.AddressLine1 || '') + (supplier?.AddressLine2 ? ', ' + supplier.AddressLine2 : '') + (supplier?.Location ? ', ' + supplier.Location : '');
  doc.setFont('helvetica', 'bold'); doc.text('Company:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text(supplierName, supDataX, sY); sY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Address:', supLabelX, sY); doc.setFont('helvetica', 'normal');
  const addrLines = doc.splitTextToSize(supplierAddr || '-', halfWidth - 31);
  doc.text(addrLines, supDataX, sY); sY += lineH * Math.max(addrLines.length, 1);
  doc.setFont('helvetica', 'bold'); doc.text('GSTIN:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text(supplier?.VATNumber || '-', supDataX, sY); sY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('IEC Code:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text(supplier?.IECCode || '-', supDataX, sY); sY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Tel / Email:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text((supplier?.ContactNumber1 || '') + ' / ' + (supplier?.Email || ''), supDataX, sY);

  yPos = boxStartY + 46;

  // === CONSIGNEE (left) + SHIPMENT DETAILS (right) ===
  const box2StartY = yPos;

  // Left: Consignee
  doc.setDrawColor(...navy);
  doc.rect(margin, box2StartY, halfWidth, 32);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('CONSIGNEE / BUYER', margin + 3, box2StartY + 5);
  doc.line(margin, box2StartY + 7, margin + halfWidth, box2StartY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.setFontSize(7.5);
  let cY = box2StartY + 12;
  const conLabelX = margin + 3;
  const conDataX = margin + 32;
  doc.setFont('helvetica', 'bold'); doc.text('Company:', conLabelX, cY); doc.setFont('helvetica', 'normal'); doc.text(branchDetails.companyName || branchDetails.branchName || '-', conDataX, cY); cY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Address:', conLabelX, cY); doc.setFont('helvetica', 'normal'); doc.text((branchDetails.addressLine1 || '') + (branchDetails.location ? ', ' + branchDetails.location : ''), conDataX, cY); cY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('TRN:', conLabelX, cY); doc.setFont('helvetica', 'normal'); doc.text(branchDetails.gstNumber || '-', conDataX, cY); cY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Importer Code:', conLabelX, cY); doc.setFont('helvetica', 'normal'); doc.text(branchDetails.iecCode || '-', conDataX, cY);

  // Right: Shipment Details
  doc.setDrawColor(...navy);
  doc.rect(rightX, box2StartY, halfWidth, 32);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('SHIPMENT DETAILS', rightX + 3, box2StartY + 5);
  doc.line(rightX, box2StartY + 7, rightX + halfWidth, box2StartY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.setFontSize(7.5);
  let shY = box2StartY + 12;
  const shipLabelX = rightX + 3;
  const shipDataX = rightX + 36;
  doc.setFont('helvetica', 'bold'); doc.text('Port of Loading:', shipLabelX, shY); doc.setFont('helvetica', 'normal'); doc.text(poData.PortOfLoading || '-', shipDataX, shY); shY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Port of Discharge:', shipLabelX, shY); doc.setFont('helvetica', 'normal'); doc.text(poData.PortOfDischarge || '-', shipDataX, shY); shY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Mode of Shipment:', shipLabelX, shY); doc.setFont('helvetica', 'normal'); doc.text(poData.ModeOfShipment || '-', shipDataX, shY); shY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Partial Shipment:', shipLabelX, shY); doc.setFont('helvetica', 'normal'); doc.text(poData.PartialShipment || '-', shipDataX, shY); shY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Transhipment:', shipLabelX, shY); doc.setFont('helvetica', 'normal'); doc.text(poData.Transhipment || '-', shipDataX, shY);

  yPos = box2StartY + 36;

  // === ITEMS TABLE ===
  const tableBody = details.map((d, idx) => {
    const itemName = d.ItemName || '-';
    const qty = parseFloat(d.Quantity) || 0;
    const rate = parseFloat(d.Rate) || 0;
    const total = parseFloat(d.TaxableAmount) || (qty * rate);
    return [idx + 1, itemName, d.HSNCode || '-', countryOfOrigin || '-', qty, rate.toFixed(2), total.toFixed(2)];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['S.No', 'Description of Goods', 'HS / Product Code', 'Country of Origin', 'Qty (Units)', 'Unit Price (' + currencyCode + ')', 'Total (' + currencyCode + ')']],
    body: tableBody,
    theme: 'grid',
    headStyles: { fillColor: navy, textColor: [255, 255, 255], fontSize: 7.5, fontStyle: 'bold', halign: 'center', cellPadding: 2 },
    bodyStyles: { fontSize: 7.5, cellPadding: 2, textColor: darkText },
    columnStyles: {
      0: { halign: 'center', cellWidth: 12 },
      1: { halign: 'left' },
      2: { halign: 'center', cellWidth: 24 },
      3: { halign: 'center', cellWidth: 26 },
      4: { halign: 'center', cellWidth: 20 },
      5: { halign: 'right', cellWidth: 26 },
      6: { halign: 'right', cellWidth: 28 }
    },
    tableWidth: contentWidth,
    margin: { left: margin, right: margin },
    styles: { lineColor: navy, lineWidth: 0.2 }
  });

  yPos = doc.lastAutoTable.finalY + 5;

  // === TOTALS ===
  const goodsTotal = details.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0);
  const freightInsurance = (parseFloat(poData.FreightAmount) || 0) + (parseFloat(poData.PackingAmount) || 0);
  const grandTotal = goodsTotal + freightInsurance;

  const totalsX = pageWidth - margin - 75;
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.3);

  // Goods Total
  doc.rect(totalsX, yPos, 75, 7);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...darkText);
  doc.text('Goods Total (' + (poData.Incoterms || 'FOB') + ', ' + currencyCode + '):', totalsX + 2, yPos + 5);
  doc.text(goodsTotal.toFixed(2), totalsX + 73, yPos + 5, { align: 'right' });

  // Freight & Insurance
  doc.rect(totalsX, yPos + 7, 75, 7);
  doc.setFont('helvetica', 'normal');
  doc.text('Freight & Insurance (if CIF/CIP):', totalsX + 2, yPos + 12);
  doc.text(freightInsurance > 0 ? freightInsurance.toFixed(2) : '-', totalsX + 73, yPos + 12, { align: 'right' });

  // Grand Total
  doc.setFillColor(240, 244, 255);
  doc.rect(totalsX, yPos + 14, 75, 8, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('Grand Total (' + currencyCode + '):', totalsX + 2, yPos + 20);
  doc.text(grandTotal.toFixed(2), totalsX + 73, yPos + 20, { align: 'right' });

  yPos += 28;

  // === TERMS & CONDITIONS ===
  const allTerms = termsDetails || [];
  if (allTerms.length > 0) {
    if (yPos > 250) { doc.addPage(); yPos = margin; }
    doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
    doc.text('International Trade Terms & Conditions', margin, yPos);
    yPos += 2;
    doc.setLineWidth(0.4);
    doc.line(margin, yPos, margin + 70, yPos);
    yPos += 4;

    doc.setFont('helvetica', 'normal'); doc.setTextColor(...darkText); doc.setFontSize(6.5);
    allTerms.forEach((t, i) => {
      if (yPos > 280) { doc.addPage(); yPos = margin; }
      const lines = doc.splitTextToSize((i + 1) + '. ' + (t.Name || ''), contentWidth);
      doc.text(lines, margin, yPos);
      yPos += lines.length * 3.2;
    });
    yPos += 3;
  }

  // === DOCUMENTS REQUIRED + AUTHORISED SIGNATORY ===
  // Check if enough space, else new page
  if (yPos > 240) { doc.addPage(); yPos = margin; }

  doc.setDrawColor(...navy);
  doc.rect(margin, yPos, halfWidth, 30);
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('DOCUMENTS REQUIRED', margin + 3, yPos + 5);
  doc.line(margin, yPos + 7, margin + halfWidth, yPos + 7);

  doc.setFont('helvetica', 'normal'); doc.setTextColor(...darkText); doc.setFontSize(7.5);
  let dY = yPos + 12;
  doc.text('- Commercial Invoice & Packing List', margin + 3, dY); dY += 4;
  doc.text('- Bill of Lading / Airway Bill', margin + 3, dY); dY += 4;
  doc.text('- Certificate of Origin', margin + 3, dY); dY += 4;
  doc.text('- Insurance Certificate (if CIF/CIP)', margin + 3, dY);

  doc.setDrawColor(...navy);
  doc.rect(rightX, yPos, halfWidth, 30);
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('AUTHORISED SIGNATORY', rightX + 3, yPos + 5);
  doc.line(rightX, yPos + 7, rightX + halfWidth, yPos + 7);

  doc.setFont('helvetica', 'bold'); doc.setTextColor(...darkText); doc.setFontSize(7.5);
  doc.text('For ' + (branchDetails.companyName || branchDetails.branchName || '') + ' (Buyer)', rightX + 3, yPos + 12);
  doc.setFont('helvetica', 'italic'); doc.setTextColor(...grayText);
  doc.text('Signature & Company Stamp', rightX + 3, yPos + 27);

  yPos += 34;

  // === NOTE ===
  yPos += 4;
  if (yPos > 270) { doc.addPage(); yPos = margin; }
  doc.setFillColor(239, 246, 255);
  doc.setDrawColor(147, 197, 253);
  doc.roundedRect(margin, yPos, contentWidth, 10, 1, 1, 'FD');
  doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('Note: ', margin + 2, yPos + 4);
  doc.setFont('helvetica', 'normal');
  doc.text('This is an export/import transaction. UAE VAT is not charged by the Supplier at source. Import VAT (if applicable) shall be self-', margin + 10, yPos + 4);
  doc.text('accounted for by the Buyer under the UAE reverse-charge mechanism at the point of customs clearance.', margin + 2, yPos + 8);

  // === WEBSITE FOOTER ===
  yPos += 16;
  if (yPos > 280) { doc.addPage(); yPos = margin; }
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('www.bpsglobaltrading.com', pageWidth / 2, yPos, { align: 'center' });

  return doc;
};

/**
 * Download International PO as PDF
 */
export const downloadInternationalPOPdf = async (poData, details, supplier, branchDetails, countryOfOrigin, termsDetails) => {
  const doc = await generateInternationalPOPdf(poData, details, supplier, branchDetails, countryOfOrigin, termsDetails);
  doc.save('International_PO_' + (poData.PurchaseOrderNo || 'draft') + '.pdf');
};

/**
 * Share International PO PDF via WhatsApp
 */
export const shareInternationalPOViaWhatsApp = async (poData, details, supplier, branchDetails, countryOfOrigin, termsDetails, phoneNumber) => {
  try {
    const doc = await generateInternationalPOPdf(poData, details, supplier, branchDetails, countryOfOrigin, termsDetails);
    const pdfBlob = doc.output('blob');
    const poNo = poData.PurchaseOrderNo || 'draft';
    const currencyName = poData.CurrencyName || supplier?.CurrencyName || '';
    const currencyCode = currencyName.toUpperCase().trim();
    const totalAmount = parseFloat(poData.TotalAmount || 0).toFixed(2);
    const taxableAmount = parseFloat(poData.TaxableAmount || 0).toFixed(2);
    const supplierName = poData.MasterName || supplier?.Name || '';

    const message = `Hello ${supplierName},\n\nPlease find the International Purchase Order ${poNo} for ${currencyCode} ${taxableAmount}.\n\nKindly acknowledge receipt and confirm.\n\nThank you.`;

    // Try native Web Share API (works on mobile with file attachment)
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], 'po.pdf', { type: 'application/pdf' })] })) {
      const file = new File([pdfBlob], `International_PO_${poNo.replace(/\//g, '_')}.pdf`, { type: 'application/pdf' });
      await navigator.share({ title: `International PO ${poNo}`, text: message, files: [file] });
      return { success: true, method: 'native' };
    } else {
      // Fallback: download PDF + open WhatsApp with message
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = phoneNumber
        ? `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${encodedMessage}`
        : `https://wa.me/?text=${encodedMessage}`;

      const fileName = `International_PO_${poNo.replace(/\//g, '_')}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      window.open(whatsappUrl, '_blank');
      return { success: true, method: 'fallback', message: 'PDF downloaded. Please attach it manually in WhatsApp.' };
    }
  } catch (error) {
    console.error('Error sharing PO via WhatsApp:', error);
    return { success: false, error: error.message };
  }
};
